/**
 * 캡슐 뽑기 이벤트 — 서버 호출 자리.
 *
 * **지금은 전부 가짜(mock)다.** 실제 API 가 생기면 함수 몸통만 바꾼다.
 * 화면은 이 파일의 함수만 부르고, 뽑을 수 있는지·당첨·꽝을 스스로 정하지 않는다.
 *
 * - 당첨 여부는 레버를 누를 때 서버에서 먼저 받아 두고, 캡슐이 열릴 때 보여준다.
 * - 교환권 사용 처리는 서버에서 1회만(중복 사용 차단). 화면은 '사용하기' → 확인 뒤
 *   redeemVoucher 를 부르고, 돌려받은 usedAt 으로 '사용 완료'를 보인다.
 *   흐르는 글자·시계는 캡처 방지용 보조 수단.
 *
 * 개발 모드에서는 GachaDebug 스위치가 sessionStorage 에 정해 둔 값을 따른다.
 * 그 분기는 `process.env.NODE_ENV === 'development'` 로 감싸 두어 운영 빌드에서
 * 통째로 사라진다. 운영 빌드의 mock 은 '뽑을 수 없음'을 돌려준다 — 실수로
 * 이대로 나가도 아무도 뽑지 못하게(버튼만 비활성, 안내 문구 없음).
 */

export type GachaResult = 'win' | 'lose'

export interface GachaVoucher {
  prize: string
  store: string
  validity: string
  usedAt: string | null
}

/** 개발 스위치가 쓰는 키. GachaDebug.tsx 와 같아야 한다. */
export const DEV_MODE_KEY = 'gacha_dev_mode'
const MOCK_VOUCHER_KEY = 'gacha_mock_voucher'

type DevMode = 'win' | 'lose'

const devMode = (): DevMode | null => {
  if (process.env.NODE_ENV === 'development') {
    return sessionStorage.getItem(DEV_MODE_KEY) === 'lose' ? 'lose' : 'win'
  }
  return null
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** 꽝 카드 진행바: 다음 뽑기까지 채운 기록 수(mock). TODO: status 응답에서 받기 */
export const MOCK_RECORD_PROGRESS = 3
export const RECORDS_PER_DRAW = 10

/** 지금 뽑을 수 있는지. TODO: GET /event/gacha/status */
export const fetchCanDraw = async (): Promise<boolean> => {
  await delay(100)
  return devMode() !== null
}

/** 레버를 돌릴 때 결과를 받는다. TODO: POST /event/gacha/draw */
export const drawGacha = async (): Promise<GachaResult> => {
  await delay(100)
  const result: GachaResult = devMode() === 'lose' ? 'lose' : 'win'
  if (result === 'win') {
    const voucher: GachaVoucher = {
      prize: '후라이드치킨 1마리',
      store: '깐부치킨 고덕역점',
      validity: '2026년 11월 30일까지',
      usedAt: null,
    }
    sessionStorage.setItem(MOCK_VOUCHER_KEY, JSON.stringify(voucher))
  }
  return result
}

/** 내 교환권. 없으면 null. TODO: GET /event/gacha/voucher */
export const fetchVoucher = async (): Promise<GachaVoucher | null> => {
  await delay(100)
  const raw = sessionStorage.getItem(MOCK_VOUCHER_KEY)
  return raw ? (JSON.parse(raw) as GachaVoucher) : null
}

/**
 * 교환권 사용 처리. 지금은 mock 이라 sessionStorage 에만 usedAt 을 적는다.
 * TODO: POST /event/gacha/voucher/use — 실제로는 서버가 1회만 처리하고(이미 썼으면 거절)
 * 사용 시각을 돌려준다. 화면은 그 값만 믿는다.
 */
export const redeemVoucher = async (): Promise<GachaVoucher | null> => {
  await delay(100)
  const v = await fetchVoucher()
  if (!v) return null
  if (!v.usedAt) {
    v.usedAt = new Date().toISOString()
    sessionStorage.setItem(MOCK_VOUCHER_KEY, JSON.stringify(v))
  }
  return v
}

/** 개발 스위치 전용 — 교환권 mock 상태를 바꾼다. */
export const __setMockVoucher = (v: GachaVoucher | null) => {
  if (v) sessionStorage.setItem(MOCK_VOUCHER_KEY, JSON.stringify(v))
  else sessionStorage.removeItem(MOCK_VOUCHER_KEY)
}
